import type { IFilterOptionDef } from 'ag-grid-community';

import type { BillingReportRow } from '../types';

import { isBlank } from './isBlank';

/**
 * A no-input filter option for the Status (`errors`) and flag (`indicators`)
 * columns. Their values are a dict and a list, which AG Grid's built-in text
 * operators can't match meaningfully -- its "blank" treats only null or an
 * empty string as blank, so a clean row's `{}` errors counts as "not blank".
 * Each option instead carries its own row predicate (for the client-side
 * grid, and the server-backed grid's re-filter of the fetched page) and the
 * server params that select the same rows (see translateFilterModel).
 */
export interface RowFlagFilterOption {
  displayKey: string;
  displayName: string;
  matches: (row: BillingReportRow) => boolean;
  params: Record<string, string>;
}

export const STATUS_FILTER_OPTIONS: RowFlagFilterOption[] = [
  {
    displayKey: 'hasErrors',
    displayName: 'Has errors',
    matches: (row) => !isBlank(row.errors),
    params: { errors_isnull: 'false' }
  },
  {
    displayKey: 'noErrors',
    displayName: 'No errors',
    matches: (row) => isBlank(row.errors),
    params: { errors_isnull: 'true' }
  },
  {
    displayKey: 'hasWarnings',
    displayName: 'Has warnings',
    matches: (row) => !isBlank(row.warnings),
    params: { warnings_isnull: 'false' }
  },
  {
    displayKey: 'noWarnings',
    displayName: 'No warnings',
    matches: (row) => isBlank(row.warnings),
    params: { warnings_isnull: 'true' }
  }
];

const indicatorOptions = (
  indicator: string,
  withName: string,
  withoutName: string
): RowFlagFilterOption[] => [
  {
    displayKey: indicator,
    displayName: withName,
    matches: (row) => (row.indicators || []).includes(indicator),
    params: { indicators_contains: indicator }
  },
  {
    displayKey: `not_${indicator}`,
    displayName: withoutName,
    matches: (row) => !(row.indicators || []).includes(indicator),
    params: { indicators_not_contains: indicator }
  }
];

// One pair per indicator IndicatorsGridCell renders.
export const INDICATOR_FILTER_OPTIONS: RowFlagFilterOption[] = [
  ...indicatorOptions('non_billable', 'Non-billable', 'Not non-billable'),
  ...indicatorOptions('has_edits', 'Has edits', 'No edits')
];

const ROW_FLAG_FILTER_OPTIONS: Record<string, RowFlagFilterOption[]> = {
  errors: STATUS_FILTER_OPTIONS,
  indicators: INDICATOR_FILTER_OPTIONS
};

export const findRowFlagFilterOption = (
  field: string,
  type: string | null | undefined
): RowFlagFilterOption | undefined =>
  ROW_FLAG_FILTER_OPTIONS[field]?.find((option) => option.displayKey === type);

/**
 * AG Grid filterParams for a row-flag column. The column's filterValueGetter
 * hands the predicate the whole row, since the Status options look at both
 * `errors` and `warnings`.
 */
export const rowFlagFilterParams = (options: RowFlagFilterOption[]) => ({
  filterOptions: options.map(({ displayKey, displayName, matches }): IFilterOptionDef => ({
    displayKey,
    displayName,
    numberOfInputs: 0,
    predicate: (_values: unknown[], row: BillingReportRow) => matches(row)
  }))
});
